import type { IColumnsPickerLiteProps } from "@similarweb/ui-components";
import { ColumnsPickerLite } from "@similarweb/ui-components";
import { SearchInput } from "@similarweb/ui-components";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import type { FunctionComponent } from "react";
import { useEffect, useRef } from "react";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { NetworksFilter } from "app/pages/website-analysis/traffic-sources/display-ads/publishers/common/filters/NetworksFilter";
import {
    DistributionSection,
    DownloadExcelContainer,
    Filters,
    FiltersGrow,
    LoaderWrapper,
    NetworksFilterWrapper,
    Right,
    SearchContainer,
    Section,
    SectionWrapper,
    Separator,
    TitleContainer,
} from "app/pages/website-analysis/analyze-publishers/advertisers/common/StyledComponents";
import { Button } from "@similarweb/ui-components";
import { CategoryFilter } from "app/pages/website-analysis/traffic-sources/display-ads/publishers/common/filters/CategoryFilter";
import type {
    IAdvertisersTableTopProps,
    IFilterItem,
} from "app/pages/website-analysis/analyze-publishers/advertisers/common/types";
import { AdvertisersOverview } from "app/pages/website-analysis/analyze-publishers/advertisers/common/AdvertisersOverview";
import { AdvertisersTopNetworksDistribution } from "app/pages/website-analysis/analyze-publishers/advertisers/single/AdvertisersTopNetworksDistribution";
import { AdvertisersTopIndustriesDistribution } from "app/pages/website-analysis/analyze-publishers/advertisers/single/AdvertisersTopIndustriesDistribution";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { WidgetTitle } from "app/components/widget-title/WidgetTitle";
import { WidgetLoader } from "app/components/widget-loader/WidgetLoader";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import { allTrackers } from "app/services/track/track";

interface IAdvertisersTableTopSingleProps extends IAdvertisersTableTopProps {
    topCategories: IFilterItem[];
    networks: IFilterItem[];
}

export const AdvertisersTableTopSingle: FunctionComponent<IAdvertisersTableTopSingleProps> = (props) => {
    const {
        isLoadingData,
        tableColumns,
        onClickToggleColumns,
        downloadExcelPermitted,
        excelLink,
        searchValue,
        chosenItems,
        i18n,
        topCategories,
        category,
        network,
        onFilterChange,
        filtersData,
        totalSectionData,
        networks,
        getLink,
        title,
        tooltip,
        subtitleFilters,
        filtersStateObject,
    } = props;
    const prevCategory = useRef(category);
    const isPaidExcelPermitted = isPaidExcelAllowed();

    const getColumnsPickerLiteProps = (): Omit<IColumnsPickerLiteProps, "translations"> => {
        const columns = tableColumns.reduce((res, col, index) => {
            if (!col.fixed) {
                return [
                    ...res,
                    {
                        key: index.toString(),
                        displayName: col.displayName,
                        visible: col.visible,
                    },
                ];
            }
            return res;
        }, []);
        return {
            columns,
            onColumnToggle,
            onPickerToggle: () => null,
            maxHeight: 264,
            width: "auto",
        };
    };
    const onColumnToggle = (key) => {
        if (tableColumns[key].visible) {
            allTrackers.dispatchTrackEvent(
                "Drop Down",
                "remove",
                `Table Columns/${tableColumns[key].displayName || ""}`,
            );
        } else {
            allTrackers.dispatchTrackEvent("Drop Down", "add", `Table Columns/${tableColumns[key].displayName || ""}`);
        }
        onClickToggleColumns(parseInt(key, 10));
    };
    const excelDownloadUrl = downloadExcelPermitted ? excelLink : "";
    const trackExcelDownload = () => {
        allTrackers.dispatchTrackEvent("Download", "submit-ok", "Table/Excel");
    };

    const onClearAll = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "Table/Clear all");

        const resetParams = {
            network: null,
            industry: null,
        };
        onFilterChange(resetParams, true);
    };

    const onNetworksFilterChange = (value) => {
        allTrackers.dispatchTrackEvent(
            "Drop Down",
            "click",
            `Table/Network/${(value ? value.children : "Clear Filter") || ""}`,
        );

        onFilterChange({ network: value && value.children }, true);
    };

    // In case a category was clicked from the MonetizationNetworksTable itself
    useEffect(() => {
        if (prevCategory.current !== category) {
            onCategoryClick(category);
        }
    }, [category]);

    const onCategoryFilterChange = (category) => {
        const value = category ? category.forApi.replace("~", " > ").replace("_", " ") : "Clear Filter";
        allTrackers.dispatchTrackEvent("Drop Down", "click", `Table/Category/${value || ""}`);

        onCategoryClick(category && category.forApi, true);
    };

    const onCategoryClick = (category, updateUrl = false) => {
        onFilterChange({ industry: category }, updateUrl);
    };

    const onSearch = (value) => {
        allTrackers.dispatchTrackEvent("Search Bar", "click", `Table/${value || ""}`);

        onFilterChange({ search: value }, true);
    };

    return !isLoadingData ? (
        <>
            <TitleContainer>
                <WidgetTitle
                    title={title}
                    tooltip={tooltip}
                    filters={subtitleFilters}
                />
            </TitleContainer>
            <SectionWrapper>
                <Section margin={15.5}>
                    <Filters>
                        <FlexRow>
                            <FiltersGrow>
                                {filtersData?.Networks && (
                                    <NetworksFilterWrapper>
                                        <NetworksFilter
                                            selectedNetwork={network}
                                            networks={filtersData.Networks}
                                            onNetworksFilterChange={onNetworksFilterChange}
                                        />
                                    </NetworksFilterWrapper>
                                )}
                                {filtersData?.Categories && (
                                    <CategoryFilter
                                        selectedCategory={category}
                                        onSelectCategory={onCategoryFilterChange}
                                        allCategories={filtersData.Categories}
                                    />
                                )}
                            </FiltersGrow>
                            <Button
                                type="flat"
                                isDisabled={!network && !category}
                                onClick={onClearAll}
                            >
                                {i18n("forms.buttons.clearall.text")}
                            </Button>
                        </FlexRow>
                    </Filters>
                </Section>
            </SectionWrapper>
            <Separator />
            {totalSectionData && (
                <Section margin={19}>
                    <AdvertisersOverview {...totalSectionData} />
                </Section>
            )}
            <Separator />
            <DistributionSection>
                <AdvertisersTopNetworksDistribution networks={networks} />
                <AdvertisersTopIndustriesDistribution
                    data={topCategories}
                    getLink={getLink}
                    onClick={onCategoryClick}
                />
            </DistributionSection>
            <SearchContainer>
                <SearchInput
                    disableClear={true}
                    defaultValue={searchValue}
                    debounce={500}
                    onChange={onSearch}
                    placeholder={i18n("forms.search.placeholder")}
                />
                <Right>
                    <DownloadExcelContainer>
                        <DownloadButtonMenu
                            Excel={true}
                            PaidExcel={isPaidExcelPermitted}
                            totalCount={totalSectionData?.totalUnGroupedCount}
                            downloadUrl={excelDownloadUrl}
                            exportFunction={trackExcelDownload}
                            excelLocked={!downloadExcelPermitted}
                        />
                    </DownloadExcelContainer>
                    <ColumnsPickerLite
                        {...getColumnsPickerLiteProps()}
                        withTooltip
                        translations={{
                            tooltip: i18n("workspace.sales.column.picker"),
                            getSelectedAmountText: ({ selectedValue, totalValue }) =>
                                i18n("common.columnsPicker.selectedAmount", {
                                    selected: selectedValue,
                                    total: totalValue,
                                }),
                            mandatoryField: i18n("common.columnsPicker.mandatoryField"),
                            apply: i18n("common.apply"),
                        }}
                    />
                    <AddToDashboard
                        metric="DashboardTopOutgoingAds"
                        modelType="fromWebsite"
                        type="Table"
                        webSource={filtersStateObject.webSource}
                        filters={{ ...filtersStateObject }}
                        overrideAddToDashboardParams={{
                            key: chosenItems.filter(({ name }) => name === filtersStateObject.key),
                        }}
                    />
                </Right>
            </SearchContainer>
        </>
    ) : (
        <LoaderWrapper>
            <WidgetLoader />
        </LoaderWrapper>
    );
};
